import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  Plus, 
  Building2, 
  Pencil, 
  Trash2, 
  RefreshCw,
  Loader2,
  Link as LinkIcon,
  Unlink,
  Download
} from "lucide-react";
import { getBanks, createBank, updateBank, deleteBank, getAvailableAspsps, connectBankAccount, getConnectedBanks, syncBankTransactions, disconnectBank, importAllBanks } from "@/lib/api";
import { toast } from "sonner";

const COLORS = [
  { name: "Émeraude", value: "#064E3B" },
  { name: "Bleu", value: "#1E40AF" },
  { name: "Violet", value: "#6D28D9" },
  { name: "Rose", value: "#BE185D" },
  { name: "Orange", value: "#C2410C" },
  { name: "Gris", value: "#374151" },
];

// Structures (SCI) — doivent correspondre aux structures Notion
const STRUCTURES = [
  "Seclin",
  "Armentières",
  "La Madeleine",
  "Roncq",
  "Hem",
  "Lambersart",
];

export default function Banks() {
  const [searchParams] = useSearchParams();
  const [banks, setBanks] = useState([]);
  const [connectedBanks, setConnectedBanks] = useState([]);
  const [availableAspsps, setAvailableAspsps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [connectDialogOpen, setConnectDialogOpen] = useState(false);
  const [editingBank, setEditingBank] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [connectLoading, setConnectLoading] = useState(false);
  const [selectedAspsp, setSelectedAspsp] = useState("");
  const [syncingAccount, setSyncingAccount] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    iban: "",
    balance: "0",
    color: "#064E3B",
    structure: ""
  });

  // Handle callback params
  useEffect(() => {
    const connected = searchParams.get("connected");
    const accounts = searchParams.get("accounts");
    const error = searchParams.get("error");
    
    if (connected === "true") {
      toast.success(`${accounts} compte(s) bancaire(s) connecté(s) !`);
      // Clear URL params
      window.history.replaceState({}, "", "/banks");
      // Auto-import : crée immédiatement les banques depuis les comptes connectés
      handleImportAll();
    } else if (error) {
      toast.error(`Erreur de connexion: ${error}`);
      window.history.replaceState({}, "", "/banks");
    }
  }, [searchParams]);

  const fetchBanks = async () => {
    setLoading(true);
    try {
      const [banksRes, connectedRes] = await Promise.all([
        getBanks(),
        getConnectedBanks()
      ]);
      setBanks(banksRes.data);
      setConnectedBanks(connectedRes.data);
    } catch (error) {
      toast.error("Erreur lors du chargement des banques");
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableAspsps = async () => {
    try {
      const response = await getAvailableAspsps("FR");
      setAvailableAspsps(response.data);
    } catch (error) {
      console.error("Error fetching ASPSPs:", error);
    }
  };



  const handleImportAll = async () => {
    try {
      const response = await importAllBanks();
      const { imported, existing } = response.data;
      if (imported > 0) {
        toast.success(`${imported} banque(s) importée(s) depuis Enable Banking !`);
        fetchBanks();
      } else if (existing > 0) {
        toast.info(`Toutes vos banques Enable Banking sont déjà importées (${existing})`);
      } else {
        toast.info("Aucune banque Enable Banking trouvée à importer");
      }
    } catch (error) {
      toast.error("Erreur lors de l'import des banques");
    }
  };

  useEffect(() => {
    fetchBanks();
    fetchAvailableAspsps();
  }, []);

  const openEditDialog = (bank) => {
    setEditingBank(bank);
    setFormData({
      name: bank.name,
      iban: bank.iban || "",
      balance: bank.balance.toString(),
      color: bank.color,
      structure: bank.structure || ""
    });
    setDialogOpen(true);
  };

  const openCreateDialog = () => {
    setEditingBank(null);
    setFormData({
      name: "",
      iban: "",
      balance: "0",
      color: "#064E3B",
      structure: ""
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);

    try {
      const data = {
        name: formData.name,
        iban: formData.iban || null,
        balance: parseFloat(formData.balance),
        color: formData.color,
        structure: formData.structure || null
      };

      if (editingBank) {
        await updateBank(editingBank.id, data);
        toast.success("Banque mise à jour");
      } else {
        await createBank(data);
        toast.success("Banque ajoutée");
      }
      
      setDialogOpen(false);
      fetchBanks();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'opération");
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async (bankId) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer cette banque ?")) return;

    try {
      await deleteBank(bankId);
      toast.success("Banque supprimée");
      fetchBanks();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const handleConnectBank = async () => {
    if (!selectedAspsp) return;
    setConnectLoading(true);
    
    try {
      const response = await connectBankAccount(selectedAspsp, "FR");
      // Redirect to bank authorization
      window.location.href = response.data.auth_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la connexion");
      setConnectLoading(false);
    }
  };

  const handleSyncTransactions = async (connectedBank, localBankId) => {
    setSyncingAccount(connectedBank.id);
    try {
      const response = await syncBankTransactions(connectedBank.account_uid, localBankId);
      toast.success(response.data.message);
      fetchBanks();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la synchronisation");
    } finally {
      setSyncingAccount(null);
    }
  };

  const handleDisconnectBank = async (connectedId) => {
    if (!window.confirm("Déconnecter ce compte bancaire ?")) return;
    try {
      await disconnectBank(connectedId);
      toast.success("Compte déconnecté");
      fetchBanks();
    } catch (error) {
      toast.error("Erreur lors de la déconnexion");
    }
  };

  const formatUpdateDate = (iso) => {
    return new Date(iso).toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: "EUR"
    }).format(amount);
  };

  const totalBalance = banks.reduce((sum, bank) => sum + bank.balance, 0);

  return (
    <div className="space-y-8 animate-fade-in" data-testid="banks-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: "Manrope" }}>
            Banques
          </h1>
          <p className="text-slate-500 mt-1">
            Gérez vos comptes bancaires
          </p>
        </div>
        <div className="flex gap-3">
          <Button 
            onClick={handleImportAll}
            variant="outline"
            className="gap-2"
            data-testid="import-banks-btn"
          >
            <Download className="w-4 h-4" />
            Importer mes banques
          </Button>
          {/* Connect Bank Dialog */}
          <Dialog open={connectDialogOpen} onOpenChange={setConnectDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="connect-bank-btn">
                <LinkIcon className="w-4 h-4 mr-2" />
                Connecter une banque
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle style={{ fontFamily: "Manrope" }}>
                  Connecter votre banque
                </DialogTitle>
                <DialogDescription>
                  Connectez votre compte bancaire pour importer automatiquement vos transactions
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Sélectionnez votre banque</Label>
                  <Select value={selectedAspsp} onValueChange={setSelectedAspsp}>
                    <SelectTrigger data-testid="select-aspsp">
                      <SelectValue placeholder="Choisir une banque..." />
                    </SelectTrigger>
                    <SelectContent>
                      {availableAspsps.map((aspsp) => (
                        <SelectItem key={aspsp.name} value={aspsp.name}>
                          {aspsp.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {availableAspsps.length === 0 && (
                    <p className="text-sm text-slate-500">Chargement des banques...</p>
                  )}
                  {availableAspsps.length > 0 && availableAspsps.length < 5 && (
                    <p className="text-xs text-orange-600">
                      Mode Sandbox : banques de test uniquement. Passez en Production pour toutes les banques.
                    </p>
                  )}
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button variant="outline" onClick={() => setConnectDialogOpen(false)}>
                    Annuler
                  </Button>
                  <Button 
                    className="bg-navy-900 hover:bg-navy-800"
                    onClick={handleConnectBank}
                    disabled={!selectedAspsp || connectLoading}
                    data-testid="confirm-connect-btn"
                  >
                    {connectLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    Connecter
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          {/* Add Manual Bank Dialog */}
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                className="bg-navy-900 hover:bg-navy-800" 
                onClick={openCreateDialog}
                data-testid="add-bank-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Ajouter manuellement
              </Button>
            </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle style={{ fontFamily: "Manrope" }}>
                {editingBank ? "Modifier la banque" : "Nouvelle banque"}
              </DialogTitle>
              <DialogDescription>
                {editingBank 
                  ? "Modifiez les informations de votre compte bancaire"
                  : "Ajoutez un nouveau compte bancaire à votre portefeuille"
                }
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Nom de la banque *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Ex: Crédit Agricole"
                  required
                  data-testid="bank-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="iban">IBAN</Label>
                <Input
                  id="iban"
                  value={formData.iban}
                  onChange={(e) => setFormData({ ...formData, iban: e.target.value })}
                  placeholder="FR76 XXXX XXXX XXXX"
                  className="font-mono"
                  data-testid="bank-iban-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="structure">Structure (SCI)</Label>
                <Select
                  value={formData.structure}
                  onValueChange={(value) => setFormData({ ...formData, structure: value })}
                >
                  <SelectTrigger id="structure" data-testid="bank-structure-select">
                    <SelectValue placeholder="Rattacher à une structure..." />
                  </SelectTrigger>
                  <SelectContent>
                    {STRUCTURES.map((s) => (
                      <SelectItem key={s} value={s}>
                        {s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="balance">Solde actuel (€)</Label>
                <Input
                  id="balance"
                  type="number"
                  step="0.01"
                  value={formData.balance}
                  onChange={(e) => setFormData({ ...formData, balance: e.target.value })}
                  data-testid="bank-balance-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Couleur</Label>
                <div className="flex gap-2 flex-wrap">
                  {COLORS.map((color) => (
                    <button
                      key={color.value}
                      type="button"
                      className={`w-8 h-8 rounded-full transition-transform ${
                        formData.color === color.value 
                          ? "ring-2 ring-offset-2 ring-slate-400 scale-110" 
                          : "hover:scale-105"
                      }`}
                      style={{ backgroundColor: color.value }}
                      onClick={() => setFormData({ ...formData, color: color.value })}
                      title={color.name}
                      data-testid={`bank-color-${color.name}`}
                    />
                  ))}
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setDialogOpen(false)}
                  data-testid="cancel-bank-btn"
                >
                  Annuler
                </Button>
                <Button 
                  type="submit" 
                  className="bg-navy-900 hover:bg-navy-800"
                  disabled={formLoading}
                  data-testid="submit-bank-btn"
                >
                  {formLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  {editingBank ? "Mettre à jour" : "Ajouter"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Connected Banks */}
      {connectedBanks.length > 0 && (
        <Card className="border-navy-200 bg-navy-50">
          <CardHeader>
            <CardTitle className="text-navy-800" style={{ fontFamily: "Manrope" }}>
              <LinkIcon className="w-5 h-5 inline mr-2" />
              Comptes connectés via Open Banking
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {connectedBanks.map((connected) => (
              <div 
                key={connected.id}
                className="flex items-center justify-between p-4 bg-white rounded-lg border border-navy-200"
                data-testid={`connected-bank-${connected.id}`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-navy-100 flex items-center justify-center">
                    <Building2 className="w-5 h-5 text-navy-700" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">{connected.bank_name}</p>
                    <p className="text-xs text-slate-500 font-mono">
                      {connected.account_iban || connected.account_uid?.slice(0, 20)}...
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Select
                    value={connected.linked_bank_id || undefined}
                    onValueChange={(bankId) => handleSyncTransactions(connected, bankId)}
                  >
                    <SelectTrigger className="w-[180px]" data-testid={`sync-select-${connected.id}`}>
                      <SelectValue placeholder="Lier à une banque..." />
                    </SelectTrigger>
                    <SelectContent>
                      {banks.map((bank) => (
                        <SelectItem key={bank.id} value={bank.id}>
                          {bank.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {syncingAccount === connected.id && (
                    <Loader2 className="w-4 h-4 animate-spin text-navy-700" />
                  )}
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleDisconnectBank(connected.id)}
                    className="text-red-500 hover:text-red-700 hover:bg-red-50"
                    data-testid={`disconnect-${connected.id}`}
                  >
                    <Unlink className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
            <p className="text-xs text-navy-700 mt-2">
              Choisissez la banque locale de destination : le lien est mémorisé et utilisé par toutes les synchronisations (manuelles et automatiques). Sélectionner lance aussi une synchronisation immédiate.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Total Balance */}
      <Card className="border-slate-200 bg-gradient-to-br from-slate-900 to-slate-800">
        <CardContent className="p-8">
          <div className="text-white">
            <p className="text-slate-400 text-sm">Solde total</p>
            <p 
              className="text-4xl font-bold mt-2" 
              style={{ fontFamily: "Manrope" }}
              data-testid="total-balance"
            >
              {formatCurrency(totalBalance)}
            </p>
            <p className="text-slate-400 text-sm mt-2">
              {banks.length} compte{banks.length > 1 ? "s" : ""} bancaire{banks.length > 1 ? "s" : ""}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Banks Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <RefreshCw className="w-8 h-8 animate-spin text-navy-900" />
        </div>
      ) : banks.length === 0 ? (
        <Card className="border-slate-200">
          <CardContent className="py-16">
            <div className="empty-state">
              <Building2 className="empty-state-icon" />
              <p className="empty-state-title">Aucune banque configurée</p>
              <p className="empty-state-text">
                Connectez votre banque via Open Banking ou ajoutez-en une manuellement
              </p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {banks.map((bank) => (
            <Card 
              key={bank.id} 
              className="border-slate-200 overflow-hidden"
              data-testid={`bank-card-${bank.id}`}
            >
              <div 
                className="h-2" 
                style={{ backgroundColor: bank.color }}
              />
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div 
                      className="w-12 h-12 rounded-full flex items-center justify-center"
                      style={{ backgroundColor: `${bank.color}20` }}
                    >
                      <Building2 className="w-6 h-6" style={{ color: bank.color }} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900">{bank.name}</h3>
                      {bank.iban && (
                        <p className="text-xs text-slate-500 font-mono">
                          •••• {bank.iban.slice(-4)}
                        </p>
                      )}
                      {bank.structure ? (
                        <span className="inline-block mt-1 px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-700">
                          {bank.structure}
                        </span>
                      ) : (
                        <span className="inline-block mt-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                          Structure non définie
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button 
                      variant="ghost" 
                      size="icon"
                      onClick={() => openEditDialog(bank)}
                      data-testid={`edit-bank-${bank.id}`}
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="icon"
                      onClick={() => handleDelete(bank.id)}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      data-testid={`delete-bank-${bank.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                <div className="pt-4 border-t border-slate-100">
                  <p className="text-sm text-slate-500">Solde</p>
                  <p
                    className={`text-2xl font-bold ${bank.balance >= 0 ? 'text-navy-700' : 'text-red-600'}`}
                    style={{ fontFamily: "Manrope" }}
                  >
                    {formatCurrency(bank.balance)}
                  </p>
                  <p className="text-xs text-slate-400 mt-1">
                    {bank.balance_updated_at
                      ? `Mis à jour le ${formatUpdateDate(bank.balance_updated_at)}`
                      : "Solde manuel — jamais synchronisé"}
                  </p>
                  <div className="flex items-center gap-2 mt-3 text-xs text-slate-500">
                    <span>{bank.month_tx_count} transaction{bank.month_tx_count > 1 ? "s" : ""} ce mois</span>
                    {bank.month_unmatched_count > 0 && (
                      <span className="px-2 py-0.5 bg-orange-50 text-orange-600 rounded-full border border-orange-200">
                        {bank.month_unmatched_count} non matchée{bank.month_unmatched_count > 1 ? "s" : ""}
                      </span>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
